import React from 'react';
import { Box, Typography } from '@mui/material';

/** Same shape as spreadsheet enrichment selection (0-based row ids). */
type EnrichmentSelectionLike = { row_ids: number[]; column_names: string[] };

/** Cell executor API / client payload: `config.cells`. */
export type CellExecutorCellSpec = { row_id: number; column_name: string };

/** Human-readable schedule fragment, e.g. "daily" → "daily" (matches confirm dialog). */
function formatScheduleRunLabel(scheduleType: string): string {
  const t = scheduleType?.trim() || '';
  if (!t) return 'on your chosen schedule';
  return t.toLowerCase();
}

/**
 * Groups cell specs by column; row numbers are shown 1-based (same as creation confirm dialog).
 */
export function groupCellSpecsByColumnForDisplay(
  cells: CellExecutorCellSpec[] | null | undefined
): Map<string, number[]> {
  const map = new Map<string, number[]>();
  for (const c of cells ?? []) {
    const rowDisplay = c.row_id + 1;
    const list = map.get(c.column_name) ?? [];
    if (!list.includes(rowDisplay)) list.push(rowDisplay);
    list.sort((a, b) => a - b);
    map.set(c.column_name, list);
  }
  return map;
}

/** `config.cells` from a cell_executor scheduled job. */
export function parseCellSpecsFromJobConfig(
  config: Record<string, unknown> | null | undefined
): CellExecutorCellSpec[] {
  return (config?.cells as CellExecutorCellSpec[] | undefined) ?? [];
}

export function enrichmentSelectionToCellSpecs(
  selection: EnrichmentSelectionLike | null
): CellExecutorCellSpec[] {
  if (!selection) return [];
  return selection.row_ids.flatMap((row_id) =>
    selection.column_names.map((column_name) => ({ row_id, column_name }))
  );
}

interface CellEnrichmentScheduleSummaryProps {
  /** e.g. job.scheduleType or 'daily' | 'weekly' | 'monthly' */
  scheduleTypeLabel: string;
  cellSpecs: CellExecutorCellSpec[] | null | undefined;
}

/**
 * Same messaging as the creation confirm dialog: schedule + "enrich the following cells" + per-column boxes.
 */
const CellEnrichmentScheduleSummary: React.FC<
  CellEnrichmentScheduleSummaryProps
> = ({ scheduleTypeLabel, cellSpecs }) => {
  const runLabel = formatScheduleRunLabel(scheduleTypeLabel);
  const byCol = groupCellSpecsByColumnForDisplay(cellSpecs);

  if (byCol.size === 0) {
    return (
      <Box sx={{ pt: 0.5 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          This schedule will run {runLabel}. No cells are configured yet.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 0.5 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        This schedule will run {runLabel}. It will enrich the following cells:
      </Typography>
      {Array.from(byCol.entries()).map(([columnName, rowNumbers]) => (
        <Box
          key={columnName}
          sx={{
            py: 0.75,
            px: 1.5,
            mb: 0.5,
            bgcolor: 'action.hover',
            borderRadius: 1
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {columnName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Rows {rowNumbers.join(', ')}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export default CellEnrichmentScheduleSummary;
